import { useCallback, useEffect, useState } from 'react';
import { BarChart3, Loader2 } from 'lucide-react';
import { getApiBase } from './apiBase';

function formatMoney(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return '—';
  return v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatMoneyShort(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return '—';
  const abs = Math.abs(v);
  const sign = v < 0 ? '-' : '';
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${sign}$${(abs / 1_000).toFixed(0)}K`;
  return `${sign}$${abs.toFixed(0)}`;
}

function truncateLabel(text, max = 18) {
  const s = String(text || '');
  return s.length <= max ? s : `${s.slice(0, max - 1)}…`;
}

function yAxisTicks(maxAbs, count = 5) {
  const ticks = [];
  for (let i = 0; i <= count; i++) {
    ticks.push(Math.round((maxAbs * i) / count));
  }
  return ticks;
}

/**
 * Bar chart of register Amount totals by Wage Type Long Text (uploaded file).
 */
export default function RegisterWageTypeChart({ registerFile, payPeriod, topN = 40 }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showAll, setShowAll] = useState(false);

  const load = useCallback(async () => {
    if (!registerFile) {
      setData(null);
      setError('');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('payrollRegister', registerFile);
      if (payPeriod) fd.append('payPeriod', payPeriod);
      fd.append('topN', String(showAll ? 200 : topN));

      const res = await fetch(`${getApiBase()}/api/register/wage-type-totals`, {
        method: 'POST',
        credentials: 'include',
        body: fd,
      });
      const json = await res.json();
      if (!res.ok) {
        const msg = json.error || 'Failed to load register totals';
        if (res.status === 404 && /wage-type-totals/i.test(msg)) {
          throw new Error(
            'Could not load register totals. Refresh the page or try again.'
          );
        }
        throw new Error(msg);
      }
      setData(json);
    } catch (e) {
      setError(e.message);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [registerFile, payPeriod, topN, showAll]);

  useEffect(() => {
    setShowAll(false);
  }, [registerFile, payPeriod]);

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  if (!registerFile) {
    return null;
  }

  const rows = showAll ? data?.totals || [] : data?.chartRows || [];
  const maxAbs = Math.max(...rows.map((r) => r.absAmount ?? Math.abs(r.amountSum)), 1);

  return (
    <div className="card-panel p-4">
      <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
        <div>
          <h3 className="flex items-center gap-2 font-semibold text-slate-100">
            <BarChart3 className="h-5 w-5 text-cyan-500" />
            Totals by wage type long text
          </h3>
          <p className="mt-1 text-xs text-slate-500">
            {registerFile.name}
            {data?.payPeriod ? ` · pay period ${data.payPeriod}` : ''}
            {data?.payPeriodAutoAdjusted ? ' (auto-selected dominant period)' : ''}
          </p>
        </div>
        {data?.truncated && !showAll && (
          <button
            type="button"
            onClick={() => setShowAll(true)}
            className="btn-secondary py-1.5 text-xs"
          >
            Show all {data.uniqueWageTypeLongText} types
          </button>
        )}
        {showAll && (
          <button
            type="button"
            onClick={() => setShowAll(false)}
            className="btn-secondary py-1.5 text-xs"
          >
            Top {data?.chartLimit || topN} only
          </button>
        )}
      </div>

      {loading && (
        <div className="flex items-center gap-2 py-6 text-sm text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin" />
          Analyzing register…
        </div>
      )}

      {error && <p className="text-sm text-red-400">{error}</p>}

      {data && !loading && !error && (
        <>
          <div className="mb-4 grid gap-3 text-xs sm:grid-cols-3">
            <div className="rounded-lg border border-slate-700 bg-slate-800/60 px-3 py-2">
              <span className="text-slate-400">Register rows</span>
              <p className="font-mono font-semibold text-slate-100">
                {data.registerRows.toLocaleString()}
                {data.registerRowsTotal !== data.registerRows
                  ? ` / ${data.registerRowsTotal.toLocaleString()} total`
                  : ''}
              </p>
            </div>
            <div className="rounded-lg border border-slate-700 bg-slate-800/60 px-3 py-2">
              <span className="text-slate-400">Wage type long texts</span>
              <p className="font-mono font-semibold text-slate-100">
                {data.uniqueWageTypeLongText.toLocaleString()}
              </p>
            </div>
            <div className="rounded-lg border border-slate-700 bg-slate-800/60 px-3 py-2">
              <span className="text-slate-400">Sum of amounts (shown)</span>
              <p className="font-mono font-semibold text-slate-100">
                ${formatMoney(rows.reduce((s, r) => s + r.amountSum, 0))}
              </p>
            </div>
          </div>

          {data.suggestedPayPeriod &&
            payPeriod &&
            payPeriod !== data.suggestedPayPeriod &&
            !data.payPeriodAutoAdjusted && (
              <p className="mb-3 text-xs text-amber-300">
                Few rows for period {payPeriod}. Dominant period in file:{' '}
                <strong>{data.suggestedPayPeriod}</strong>.
              </p>
            )}

          <p className="mb-2 text-xs text-slate-500">
            Vertical bars — height by amount (cyan = positive, rose = negative). Hover a bar for details.
          </p>
          <div className="flex gap-2">
            <div className="flex w-14 shrink-0 flex-col justify-between py-1 text-right text-[10px] text-slate-300">
              {[...yAxisTicks(maxAbs)].reverse().map((tick) => (
                <span key={tick} className="font-mono leading-none">
                  {formatMoneyShort(tick)}
                </span>
              ))}
            </div>
            <div className="min-w-0 flex-1 overflow-x-auto pb-1">
              <div
                className="flex items-end gap-1 rounded-t-lg border-b border-slate-600 bg-gradient-to-t from-slate-800/90 to-slate-900/40 px-1"
                style={{ height: '22rem', minWidth: `${Math.max(rows.length * 52, 320)}px` }}
              >
                {rows.map((row) => {
                  const abs = row.absAmount ?? Math.abs(row.amountSum);
                  const pct = Math.max(4, (100 * abs) / maxAbs);
                  const negative = row.amountSum < 0;
                  return (
                    <div
                      key={row.wageTypeLongText}
                      className="group flex h-full min-w-[48px] max-w-[72px] flex-1 flex-col items-center justify-end"
                      title={`${row.wageTypeLongText}\n$${formatMoney(row.amountSum)}\n${row.lineCount.toLocaleString()} lines`}
                    >
                      <span
                        className={`mb-1 max-w-full truncate text-center text-[9px] font-mono tabular-nums ${
                          negative ? 'text-rose-300' : 'text-slate-200'
                        }`}
                      >
                        {formatMoneyShort(row.amountSum)}
                      </span>
                      <div
                        className={`w-8 min-w-[1.75rem] max-w-full rounded-t transition-all ${
                          negative ? 'bg-rose-400 hover:bg-rose-500' : 'bg-cyan-500 hover:bg-cyan-600'
                        }`}
                        style={{ height: `${pct}%` }}
                      />
                    </div>
                  );
                })}
              </div>
              <div
                className="flex gap-1 px-1 pt-2"
                style={{ minWidth: `${Math.max(rows.length * 52, 320)}px` }}
              >
                {rows.map((row) => (
                  <div
                    key={`${row.wageTypeLongText}-lbl`}
                    className="flex min-w-[48px] max-w-[72px] flex-1 items-start justify-center"
                  >
                    <span
                      className="block max-h-24 origin-top-left -rotate-45 text-left text-[10px] font-medium leading-tight text-slate-200"
                      style={{ width: '5.5rem' }}
                      title={row.wageTypeLongText}
                    >
                      {truncateLabel(row.wageTypeLongText, 22)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <p className="mt-2 text-center text-xs font-medium text-slate-400">Wage type long text</p>

          {rows.length === 0 && (
            <p className="py-6 text-center text-sm text-slate-500">
              No rows for this pay period in the uploaded register.
            </p>
          )}
        </>
      )}
    </div>
  );
}
